import {
    Breakpoints,
    CTA,
    Text,
    TextTag,
    TokenTextAppearance,
} from '@volkswagen-onehub/components-core'
import styled, {css} from 'styled-components'
import React from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useLocation, useHistory} from 'react-router-dom'
import containerBottomStage from '../../../assets/css-vars/containerBottomStage'
import BreadcrumbsCommon from '../../../common/components/breadcrumbs/BreadcrumbsCommon'
import {setSingleCar} from '../../../features/general/generalSlice'
import TrackingAction from '../../../tracking-manager'
import BreadcrumbsInterface from '../../../types/BreadcrumbsInterface'
import {useFeatureServices} from '../../../use-feature-services'
import Brands from '../../../utils/brand/brand'
import {CustomerTypesUrl} from '../../../utils/customerTypes/customerTypes'
import getCustomerTypeFromUrl from '../../../utils/customerTypes/getCustomerTypeFromUrl'
import stripTags from '../../../utils/text/stripTags'
import {getVehiclePromotionsButtonClickData} from '../../../utils/tracking/getTrackingData'
import setTracking, {
    checkTrackingManager,
} from '../../../utils/tracking/tracking-utils/setTracking'
import UrlRoutes from '../../../utils/url/urlRoutes'
import {RootState} from '../../rootReducer'
import Breadcrumbs from './Breadcrumbs'
import DisclaimerButton from '../layers/disclaimerButton'
import isEmpty from 'lodash.isempty'
import STYlE_CONSTANTS from '../../../utils/style/styleConstants'
import {openCouponLayer} from '../layers/launchers/CouponRequestLayerHandle'
import {useTranslation} from 'react-i18next'
import {setFormCycleID} from '../../../features/userData/userDataSlice'
import {generateRandomString} from '../../../utils/tracking/tracking-utils/generateRandomString'
import {Global} from '../../../shared/variables'
import {globalColors} from '../../../utils/style/globalColors'

const BasicStageContainer = styled.div``
const BasicStageWrapper = styled.section<{$whiteBackground?: boolean}>`
    position: relative;
    padding: 0;
    background-color: ${({$whiteBackground, theme}) =>
        $whiteBackground
            ? globalColors.white000
            : theme.colors.background.primary};
    border-block-end: 1px solid ${(props) => props.theme.colors.border.divider};
`
const BasicStageMedia = styled.div`
    overflow: hidden;
    position: relative;
    height: 65vh;
    padding: 0;

    @media only screen and (min-width: ${Breakpoints.b560}px) {
        height: 70vh;
    }

    @media only screen and (min-width: ${Breakpoints.b960}px) {
        height: 75vh;
    }

    @media only screen and (min-width: ${Breakpoints.b1600}px) {
        height: 65vh;
    }

    img {
        width: 100%;
        height: 100%;
        max-width: 100%;
        object-fit: cover;
        object-position: 50% 50%;
    }
`
const BasicStageContent = styled.div`
    padding-block: 16px;
    padding-inline: ${(props) => props.theme.size.grid002};
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 24px;

    @media only screen and (min-width: ${Breakpoints.b1280}px) {
        flex-wrap: nowrap;
        -webkit-box-align: start;
        align-items: start;
    }

    @media only screen and (min-width: ${Breakpoints.b1600}px) {
        padding-block: 24px;
    }
`
const StageParallaxTextShared = css`
    font-family: ${(props) => props.theme.fonts.copy[200].fontFamily};
    font-size: ${(props) => props.theme.size.static300};
    font-weight: ${STYlE_CONSTANTS.TEXT_WEIGHT_REGULAR};
`
const StageParallaxText = styled.p`
    ${StageParallaxTextShared};
    display: none;
    line-height: 1.6;
    margin: -0.11em 0 0.11em;
    word-break: inherit;

    @media only screen and (min-width: ${Breakpoints.b560}px) {
        display: initial;
    }
`
const StageCTAContainer = styled.div`
    gap: 16px;
    display: flex;
    width: 100%;

    @media only screen and (min-width: ${Breakpoints.b560}px) {
        width: auto;
    }

    @media only screen and (min-width: ${Breakpoints.b1280}px) {
        flex-wrap: nowrap;
        flex-shrink: 0;
        padding-top: 8px;
    }

    a {
        width: 100%;
        @media only screen and (min-width: ${Breakpoints.b560}px) {
            width: auto;
        }
    }
`
const BreadcrumbsWrapper = styled.section<{$whiteBackground?: boolean}>`
    ${containerBottomStage};
    background-color: ${({$whiteBackground, theme}) =>
        $whiteBackground
            ? globalColors.white000
            : theme.colors.background.primary};
    padding-top: 16px;
    padding-bottom: 10vh;
    display: none;

    @media (min-width: ${Breakpoints.b560}px) {
        display: block;
    }
`

export interface BasicStageProps {
    title: string
    promotionText?: string
    ctaText?: string
    ctaUrl?: string
    imageName: string
    imageNameMobile?: string
    imageAlt: string
    modelName?: string
    modelSlug?: string
    disclaimer?: string
    showRFQForm?: boolean
    whiteBackground?: boolean
}

export default function BasicStage(props: BasicStageProps): React.JSX.Element {
    const {t} = useTranslation('app')
    const {tracking: trackingManager} = useFeatureServices()
    const isShowroom = useSelector(
        (state: RootState) => state.general.isShowroom
    )
    const trackingBrand = useSelector(
        (state: RootState) => state.general.trackingBrand
    )
    const history = useHistory()
    const dispatch = useDispatch()
    const model = useSelector((state: RootState) => state.general.singleCar)
    const brandFromState = useSelector(
        (state: RootState) => state.general.brand
    )
    const location = useLocation()
    const showLinkCta = !props.showRFQForm && props.ctaText && props.ctaUrl
    const showRfqCta = props.showRFQForm && model?.enable_quotation_cta_on_stage

    const listBreadcrumbs = (): BreadcrumbsInterface[] => {
        const breadcrumbs: BreadcrumbsInterface[] = [
            {
                label: BreadcrumbsCommon.HOME_LINK_LABEL,
                href:
                    brandFromState === Brands.VW
                        ? BreadcrumbsCommon.HOME_LINK_URL
                        : BreadcrumbsCommon.HOME_VIC_LINK_URL,
            },
            {
                label: BreadcrumbsCommon.PROMOTIONS_LISTING_LINK_LABEL,
                href: BreadcrumbsCommon.PROGRAMMATIC_NAVIGATION_LINK,
                onClick: () => {
                    if (
                        brandFromState === Brands.VIC &&
                        location &&
                        location.pathname
                    ) {
                        const customerFromCurrentLocation =
                            getCustomerTypeFromUrl(location)
                        const customerUrl =
                            // eslint-disable-next-line security/detect-object-injection
                            CustomerTypesUrl[customerFromCurrentLocation]

                        history.push(
                            `${BreadcrumbsCommon.PROMOTIONS_LISTING_LINK_ROUTE}${UrlRoutes.CUSTOMER_TYPE}/${customerUrl}`
                        )
                    } else {
                        history.push(
                            BreadcrumbsCommon.PROMOTIONS_LISTING_LINK_ROUTE
                        )
                    }
                    dispatch(setSingleCar({}))
                },
            },
        ]

        if (!isEmpty(model.related_promotions)) {
            const MODEL_BREADCRUMB: BreadcrumbsInterface = {
                label: model.name,
            }

            breadcrumbs.push(MODEL_BREADCRUMB)
        }

        return breadcrumbs
    }

    function trackStageCta(): void {
        const ctaLabel = showRfqCta
            ? t('cta.singlePromotion.requestQuote')
            : props.ctaText

        if (
            checkTrackingManager(trackingManager) &&
            ctaLabel &&
            props.modelName &&
            props.modelSlug
        ) {
            const clickData = getVehiclePromotionsButtonClickData(
                ctaLabel,
                props.modelName,
                props.modelSlug,
                trackingBrand,
                isShowroom,
                stripTags(props.title)
            )

            setTracking(
                trackingManager,
                TrackingAction.VWBasic_VehiclePromotionsButton_Click,
                clickData
            )
        }
    }

    return (
        <BasicStageContainer>
            <BasicStageWrapper
                id="basic-stage"
                $whiteBackground={props.whiteBackground}
            >
                <BasicStageMedia
                    className="scroll"
                    data-direction="vertical"
                    data-ratio="0.42"
                >
                    <picture>
                        <source
                            srcSet={`${props.imageNameMobile}`}
                            media="(max-width: 767px)"
                        />
                        <img src={props.imageName} alt={props.imageAlt} />
                    </picture>

                    {props.disclaimer && (
                        <DisclaimerButton disclaimer={model.disclaimer} />
                    )}
                </BasicStageMedia>

                <BasicStageContent>
                    {props.title ? (
                        <Text
                            tag={TextTag.h1}
                            appearance={TokenTextAppearance.headline400}
                            bold
                        >
                            {props.title}
                        </Text>
                    ) : undefined}

                    {props.promotionText && props.promotionText.length ? (
                        <StageParallaxText
                            dangerouslySetInnerHTML={{
                                __html: props.promotionText,
                            }}
                        />
                    ) : undefined}

                    {showLinkCta && (
                        <StageCTAContainer>
                            <CTA
                                tag="a"
                                href={props.ctaUrl}
                                aria-label={props.ctaText}
                                emphasis="primary"
                                onClick={trackStageCta}
                            >
                                {props.ctaText}
                            </CTA>
                        </StageCTAContainer>
                    )}

                    {showRfqCta && (
                        <StageCTAContainer>
                            <CTA
                                tag="button"
                                emphasis="primary"
                                aria-label={t(
                                    'cta.singlePromotion.requestQuote'
                                )}
                                onClick={() => {
                                    dispatch(
                                        setFormCycleID(
                                            generateRandomString(
                                                Global.CYCLE_ID_LENGTH
                                            )
                                        )
                                    )
                                    openCouponLayer()
                                    trackStageCta()
                                }}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            'cta.singlePromotion.requestQuote'
                                        ),
                                    }}
                                />
                            </CTA>
                        </StageCTAContainer>
                    )}
                </BasicStageContent>
            </BasicStageWrapper>
            <BreadcrumbsWrapper $whiteBackground={props.whiteBackground}>
                <Breadcrumbs
                    list={listBreadcrumbs()}
                    onBack={() => dispatch(setSingleCar({}))}
                />
            </BreadcrumbsWrapper>
        </BasicStageContainer>
    )
}
